import React from "react";
import useInquiryForm from "@/hooks/useInquiryForm";
import { whatsappWith, openExternal } from "@/lib/contact";
import { MessageCircle } from "lucide-react";

const inputCls =
  "w-full bg-[#0A0A0C] border border-white/10 px-4 h-11 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF5722] transition-colors font-mono";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="label-overline mb-2">{label}</div>
      {children}
    </label>
  );
}

const PROJECT_TYPES = [
  "Engineering Prototype",
  "College / STEM Project",
  "Home Decor / Lighting",
  "CAD Modeling / Design",
  "Other",
];

const buildWhatsAppMessage = ({ name, phone, email, project_type, message }) =>
  `Hi 3D PRINT HUB, new enquiry:
• Name: ${name}
• Phone: ${phone}${email ? `\n• Email: ${email}` : ""}
• Project: ${project_type}

${message}`;

export default function ContactForm() {
  const { form, status, update, submit } = useInquiryForm();
  const isLoading = status.state === "loading";

  const onSubmit = async (e) => {
    e.preventDefault();
    // Save to backend first, then bounce the visitor into WhatsApp with
    // their message pre-filled so it reaches the studio owner instantly.
    await submit(e);
    if (!form.name || !form.phone || !form.message) return;
    const url = whatsappWith(buildWhatsAppMessage(form));
    openExternal(null, url);
  };

  return (
    <form
      data-testid="inquiry-form"
      onSubmit={onSubmit}
      className="border border-white/10 bg-[#0C0C0E] p-6 sm:p-8 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-overline mb-2">Request a quote</div>
          <h3 className="font-display text-2xl">Tell us about the print</h3>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            Sends to our WhatsApp instantly · reply within 24h
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Your name" testid="form-name">
          <input required value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            className={inputCls} placeholder="Arjun K."
            data-testid="form-name-input" />
        </Field>
        <Field label="Phone / WhatsApp" testid="form-phone">
          <input required value={form.phone}
            onChange={(e) => update({ phone: e.target.value })}
            className={inputCls} placeholder="+91 9xxxx xxxxx"
            data-testid="form-phone-input" />
        </Field>
        <Field label="Email (optional)" testid="form-email">
          <input type="email" value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            className={inputCls} placeholder="you@domain.com"
            data-testid="form-email-input" />
        </Field>
        <Field label="Project type" testid="form-project-type">
          <select value={form.project_type}
            onChange={(e) => update({ project_type: e.target.value })}
            className={inputCls} data-testid="form-project-type-input">
            {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Brief" testid="form-message">
            <textarea required rows={5} value={form.message}
              onChange={(e) => update({ message: e.target.value })}
              className={`${inputCls} resize-none`}
              placeholder="Material, size, deadline, finish, link to STL / sketch…"
              data-testid="form-message-input" />
          </Field>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <button
          type="submit"
          data-testid="form-submit-button"
          disabled={isLoading}
          className="inline-flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#F4511E] disabled:opacity-50 text-white px-6 h-12 text-sm font-mono uppercase tracking-[0.2em] transition-colors"
        >
          <MessageCircle size={14} />
          {isLoading ? "Sending…" : "Send via WhatsApp"}
        </button>
        {status.state !== "idle" && (
          <div
            data-testid="form-status"
            className={`text-xs font-mono ${
              status.state === "success" ? "text-[#10B981]" : "text-[#FF5722]"
            }`}
          >
            {status.msg}
          </div>
        )}
      </div>
    </form>
  );
}
